import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banknote, Eye, FileText, Gavel, ReceiptText, RotateCcw, ScrollText } from 'lucide-react';
import { Pestanas } from '@/shared/ui/Pestanas';
import { FacturasDelArt8 } from './FacturasDelArt8';
import { api } from '@/shared/api/cliente';
import { useSesion } from '@/shared/sesion/SesionProvider';
import { VistaPreviaDeImpresion } from '@/shared/ui/VistaPreviaDeImpresion';
import { useRecibo } from '@/modules/recaudacion/useRecibo';
import { ModalDeCobroDeTitulos } from './ModalDeCobroDeTitulos';
import { ModalDeExtincion } from './ModalDeExtincion';
import { ModalDePagoAnterior } from './ModalDePagoAnterior';
import { ResolucionesDelArt8 } from './ResolucionesDelArt8';
import { ErrorDeApi } from '@/shared/api/problema';
import { notificar } from '@/shared/ui/avisos';
import { BotonDeFila } from '@/shared/ui/BotonDeFila';
import { CampoDeBusqueda } from '@/shared/ui/CampoDeBusqueda';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { PanelLateral } from '@/shared/ui/PanelLateral';
import { Tabla, type Columna, type OrdenDeTabla } from '@/shared/ui/Tabla';
import { useValorDiferido } from '@/shared/ui/useValorDiferido';
import {
  FORMAS,
  type FormaDeExtincion,
  type ListadoDeTitulos,
  type Resolucion,
  type ResumenDeTitulos,
  type Situacion,
  type Titulo,
} from './contrato';

export function PaginaTitulos() {
  const [vista, setVista] = useState<'titulos' | 'facturas' | 'resoluciones'>('titulos');
  const [texto, setTexto] = useState('');
  const [situacion, setSituacion] = useState<Situacion>('PENDIENTES');
  const [forma, setForma] = useState<FormaDeExtincion | ''>('');
  const [orden, setOrden] = useState<OrdenDeTabla | null>(null);
  const [pagina, setPagina] = useState(0);
  const [tamanio, setTamanio] = useState(25);
  const [abierto, setAbierto] = useState<Titulo | null>(null);

  const [cobrando, setCobrando] = useState<string | null>(null);

  const [extinguiendo, setExtinguiendo] = useState<string | null>(null);

  const [regularizando, setRegularizando] = useState<string | null>(null);
  const [constancia, setConstancia] = useState<{ titulo: string; fichero: Blob | null } | null>(
    null,
  );
  const recibo = useRecibo();
  const clienteDeConsultas = useQueryClient();
  const { puede } = useSesion();
  const puedeCobrar = puede('titulos.cobrar');
  const puedeExtinguir = puede('titulos.extinguir');
  const puedeRegularizar = puede('titulos.regularizar');
  const puedeRevertir = puede('titulos.revertir');

  async function verConstancia(r: Resolucion) {
    const titulo = r.tipo === 'PRESCRIPCION' ? 'Constancia de prescripción' : 'Constancia de baja';
    setConstancia({ titulo, fichero: null });
    try {
      const fichero = await api.descargar(`/titulos/resoluciones/${r.id}/constancia.pdf`);
      setConstancia({ titulo, fichero });
    } catch (causa) {
      setConstancia(null);
      notificar.error(
        causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO ARMAR LA CONSTANCIA',
      );
    }
  }

  const buscado = useValorDiferido(texto.trim());

  const textoValido = buscado.length === 0 || /^\d+$/.test(buscado) || buscado.length >= 3;

  const resumen = useQuery({
    queryKey: ['titulos', 'resumen'],
    queryFn: () => api.get<ResumenDeTitulos>('/titulos/resumen'),
  });

  const listado = useQuery({
    queryKey: ['titulos', buscado, situacion, forma, orden, pagina, tamanio],
    enabled: textoValido,
    queryFn: () =>
      api.get<ListadoDeTitulos>('/titulos', {
        query: {
          texto: buscado,
          situacion,
          forma: situacion === 'PENDIENTES' ? '' : forma,
          orden: orden?.campo ?? '',
          direccion: orden?.descendente ? 'desc' : 'asc',
          pagina,
          tamanio,
        },
      }),
    placeholderData: (anterior) => anterior,
  });

  const totales = listado.data?.totales;
  const columnas: Columna<Titulo>[] = [
    {
      clave: 'codigo',
      encabezado: 'Código',
      orden: 'codigo',
      contenido: (t) => (
        <span className="importe text-xs font-semibold text-armazon-700">{t.codigo ?? '—'}</span>
      ),
      pie: <span className="uppercase">Total del filtro</span>,
    },
    {
      clave: 'contribuyente',
      encabezado: 'Contribuyente',
      orden: 'nombre',
      contenido: (t) => (
        <span className="block min-w-0">
          <span className="block truncate text-xs font-medium uppercase text-armazon-700">
            {t.nombre ?? 'Sin nombre en el origen'}
          </span>
          {t.identificacion ? (
            <span className="importe block text-[10px] text-armazon-400">{t.identificacion}</span>
          ) : (

            <span className="mt-0.5 inline-block rounded bg-amber-50 px-1 text-[9px] font-bold uppercase text-amber-700">
              Sin cédula
            </span>
          )}
        </span>
      ),
      pie: totales && (
        <span className="text-[11px] font-semibold uppercase text-armazon-500">
          {totales.titulos.toLocaleString('es-EC')} títulos
        </span>
      ),
    },
    {
      clave: 'periodo',
      encabezado: 'Período',
      orden: 'periodo',
      contenido: (t) => (
        <span className="block">
          <span className="importe block text-xs text-armazon-600">
            {enMes(t.desde)}
            {t.hasta && t.hasta !== t.desde ? ` – ${enMes(t.hasta)}` : ''}
          </span>
          {t.meses != null && (
            <span className="block text-[10px] uppercase text-armazon-400">
              {t.meses} {t.meses === 1 ? 'mes' : 'meses'}
            </span>
          )}
        </span>
      ),
    },
    {
      clave: 'capital',
      encabezado: 'Capital',
      orden: 'capital',
      alinear: 'derecha',
      contenido: (t) => (
        <span className="importe text-xs text-armazon-700">{enDinero(t.capital)}</span>
      ),
      pie: totales && <span className="importe">{enDinero(totales.capital)}</span>,
    },
    {
      clave: 'situacion',
      encabezado: 'Situación',
      orden: 'extincion',
      alinear: 'centro',
      contenido: (t) =>
        t.extincion ? (
          <span className="block">
            <span className="rounded-md bg-marca-50 px-2 py-0.5 text-[10px] font-bold uppercase text-marca-700">
              {t.extincion.etiqueta}
            </span>
            <span className="importe mt-0.5 block text-[10px] text-armazon-400">
              {enFecha(t.extincion.fecha)}
            </span>
          </span>
        ) : (
          <span className="rounded-md bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-700">
            Pendiente
          </span>
        ),
    },
    {
      clave: 'saldo',
      encabezado: 'Saldo',
      alinear: 'derecha',
      contenido: (t) => (
        <span
          className={`importe text-xs font-bold ${t.saldo > 0 ? 'text-armazon-900' : 'text-armazon-300'}`}
        >
          {enDinero(t.saldo)}
        </span>
      ),
      pie: totales && <span className="importe text-armazon-900">{enDinero(totales.saldo)}</span>,
    },
    {
      clave: 'acciones',
      encabezado: '',
      alinear: 'derecha',
      clase: 'w-28',
      contenido: (t) => (
        <span className="flex justify-end gap-1">
          {puedeCobrar && !t.extincion && (
            <BotonDeFila
              icono={Banknote}
              titulo="Cobrar"

              alPulsar={() => setCobrando(t.identificacion ?? t.codigo ?? '')}
            />
          )}
          {puedeExtinguir && !t.extincion && (
            <BotonDeFila
              icono={Gavel}
              titulo="Prescripción o baja"
              alPulsar={() => setExtinguiendo(t.identificacion ?? t.codigo ?? '')}
            />
          )}
          {puedeRegularizar && !t.extincion && (
            <BotonDeFila
              icono={ReceiptText}
              titulo="Ya pagado en el sistema anterior"
              alPulsar={() => setRegularizando(t.identificacion ?? t.codigo ?? '')}
            />
          )}
          <BotonDeFila icono={Eye} titulo="Ver el título" alPulsar={() => setAbierto(t)} />
        </span>
      ),
    },
  ];

  function reiniciar<V>(fijar: (v: V) => void) {
    return (v: V) => {
      fijar(v);
      setPagina(0);
    };
  }

  return (
    <>
      <EncabezadoDePagina
        icono={ScrollText}
        color="ambar"
        titulo="Títulos Art. 8"
        subtitulo="La cartera heredada del SIRE · sin interés, por resolución de EPAGAL"
        acciones={
          <span className="flex gap-2">
            {puedeRegularizar && (
              <button
                type="button"
                onClick={() => setRegularizando('')}
                className="inline-flex h-10 items-center gap-2 rounded-lg border border-armazon-300 bg-white px-4 text-xs font-bold uppercase text-armazon-700 shadow-sm transition-colors hover:bg-armazon-50"
              >
                <ReceiptText className="size-4" aria-hidden />
                Ya pagado antes
              </button>
            )}
            {puedeExtinguir && (
              <button
                type="button"
                onClick={() => setExtinguiendo('')}
                className="inline-flex h-10 items-center gap-2 rounded-lg border border-armazon-300 bg-white px-4 text-xs font-bold uppercase text-armazon-700 shadow-sm transition-colors hover:bg-armazon-50"
              >
                <Gavel className="size-4" aria-hidden />
                Prescripción o baja
              </button>
            )}
            {puedeCobrar && (
              <button
                type="button"
                onClick={() => setCobrando('')}
                className="inline-flex h-10 items-center gap-2 rounded-lg bg-marca-600 px-4 text-xs font-bold uppercase text-white shadow-sm transition-colors hover:bg-marca-700"
              >
                <Banknote className="size-4" aria-hidden />
                Registrar cobro
              </button>
            )}
          </span>
        }
      />

      <Pestanas
        pestanas={[
          { clave: 'titulos', rotulo: 'Títulos', descripcion: 'Lo que se debe', icono: ScrollText },
          {
            clave: 'facturas',
            rotulo: 'Facturas del Art. 8',
            descripcion: 'Lo cobrado, ante el SRI',
            icono: FileText,
          },
          {
            clave: 'resoluciones',
            rotulo: 'Prescripciones y bajas',
            descripcion: 'Lo extinguido sin cobrar',
            icono: Gavel,
          },
        ]}
        activa={vista}
        alElegir={setVista}
        etiqueta="Qué ver"
      />

      {vista === 'facturas' ? (
        <FacturasDelArt8 />
      ) : vista === 'resoluciones' ? (
        <ResolucionesDelArt8 alVerConstancia={(r) => void verConstancia(r)} />
      ) : (
        <>
          <Resumen datos={resumen.data} />

          <Tabla
            datos={listado.data}
            cargando={listado.isPending && textoValido}
            columnas={columnas}
            tamanio={tamanio}
            alCambiarPagina={setPagina}
            alCambiarTamanio={reiniciar(setTamanio)}
            orden={orden}
            alOrdenar={reiniciar(setOrden)}
            entidad="títulos"
            iconoVacio={ScrollText}
            mensajeVacio={textoValido ? 'Ningún título coincide' : 'Escriba al menos tres letras'}
            herramientas={
              <span className="flex flex-wrap items-center gap-2">
                <CampoDeBusqueda
                  valor={texto}
                  alCambiar={reiniciar(setTexto)}
                  marcador="Cédula, código anterior o nombre…"
                  className="w-72"
                />
                <Selector
                  valor={situacion}
                  etiqueta="Situación"
                  opciones={[
                    { valor: 'PENDIENTES', etiqueta: 'Pendientes' },
                    { valor: 'EXTINGUIDOS', etiqueta: 'Extinguidos' },
                    { valor: 'TODOS', etiqueta: 'Todos' },
                  ]}
                  alCambiar={reiniciar((v: string) => setSituacion(v as Situacion))}
                />
                {situacion !== 'PENDIENTES' && (
                  <Selector
                    valor={forma}
                    etiqueta="Cómo se extinguió"
                    opciones={[{ valor: '', etiqueta: 'Cualquier forma' }, ...FORMAS]}
                    alCambiar={reiniciar((v: string) => setForma(v as FormaDeExtincion | ''))}
                  />
                )}
              </span>
            }
          />
        </>
      )}

      {abierto && (
        <DetalleDelTitulo
          titulo={abierto}
          puedeRevertir={puedeRevertir}
          alCerrar={() => setAbierto(null)}
          alRevertido={() => {
            setAbierto(null);
            void clienteDeConsultas.invalidateQueries({ queryKey: ['titulos'] });
          }}
        />
      )}

      {cobrando !== null && (
        <ModalDeCobroDeTitulos
          textoInicial={cobrando}
          alCerrar={() => setCobrando(null)}
          alCobrado={(pago) => {
            setCobrando(null);
            void clienteDeConsultas.invalidateQueries({ queryKey: ['titulos'] });
            void clienteDeConsultas.invalidateQueries({ queryKey: ['jornada-de-caja'] });
            void recibo.abrir(pago.id, pago.numeroComprobante);
          }}
        />
      )}

      {regularizando !== null && (
        <ModalDePagoAnterior
          textoInicial={regularizando}
          alCerrar={() => setRegularizando(null)}
          alRegistrar={() => {
            void clienteDeConsultas.invalidateQueries({ queryKey: ['titulos'] });
          }}
        />
      )}

      {extinguiendo !== null && (
        <ModalDeExtincion
          textoInicial={extinguiendo}
          alCerrar={() => setExtinguiendo(null)}
          alRegistrar={() => {
            void clienteDeConsultas.invalidateQueries({ queryKey: ['titulos'] });
            void clienteDeConsultas.invalidateQueries({ queryKey: ['titulos-resoluciones'] });
          }}
          alVerConstancia={(r) => void verConstancia(r)}
        />
      )}

      <VistaPreviaDeImpresion
        abierto={constancia !== null}
        alCerrar={() => setConstancia(null)}
        titulo={constancia?.titulo ?? ''}
        subtitulo="Para entregar al contribuyente"
        fichero={constancia?.fichero ?? null}
        cargando={constancia !== null && constancia.fichero === null}
        formato="hoja"
        textoDeCarga="Preparando la constancia…"
      />

      <VistaPreviaDeImpresion
        abierto={recibo.recibo !== null}
        alCerrar={recibo.cerrar}
        titulo="Recibo de cobro"
        subtitulo={
          recibo.recibo?.comprobante
            ? `Nº ${recibo.recibo.comprobante} · Ticket de 58 mm`
            : 'Ticket de 58 mm'
        }
        fichero={recibo.recibo?.fichero ?? null}
        cargando={recibo.preparando !== null}
      />
    </>
  );
}

function Resumen({ datos }: { datos: ResumenDeTitulos | undefined }) {
  const pagados = datos?.extinguidos.filter((e) =>
    ['PAGO_SIRE', 'PAGO_API_GIRS', 'PAGO_ANTERIOR', 'COBRO'].includes(e.forma),
  );
  const cobrado = pagados?.reduce((suma, e) => suma + e.capital, 0) ?? 0;
  const interes = pagados?.reduce((suma, e) => suma + e.interes, 0) ?? 0;
  const titulosPagados = pagados?.reduce((suma, e) => suma + e.titulos, 0) ?? 0;

  return (
    <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
      <Cifra
        valor={datos ? enDinero(datos.saldo) : '…'}
        rotulo="Por cobrar"
        nota="Capital de los títulos pendientes"
        destacada
      />
      <Cifra
        valor={datos ? datos.pendientes.toLocaleString('es-EC') : '…'}
        rotulo="Títulos pendientes"
        nota={datos ? `De ${datos.personas.toLocaleString('es-EC')} personas con cédula` : ''}
      />
      <Cifra
        valor={datos ? enDinero(cobrado) : '…'}
        rotulo="Cobrado"
        nota={datos ? `${titulosPagados.toLocaleString('es-EC')} títulos pagados` : ''}
      />
      <Cifra
        valor={datos ? enDinero(interes) : '…'}
        rotulo="Interés que cobró el SIRE"
        nota="Histórico · hoy no se cobra"
      />
    </div>
  );
}

function Cifra({
  valor,
  rotulo,
  nota,
  destacada = false,
}: {
  valor: string;
  rotulo: string;
  nota: string;
  destacada?: boolean;
}) {
  return (
    <div
      className={`rounded-xl border p-4 ${
        destacada ? 'border-amber-200 bg-amber-50' : 'border-armazon-200 bg-white'
      }`}
    >
      <span
        className={`importe block text-xl font-bold ${destacada ? 'text-amber-800' : 'text-armazon-800'}`}
      >
        {valor}
      </span>
      <span className="block text-[10px] font-semibold uppercase text-armazon-600">{rotulo}</span>
      <span className="block text-[9px] uppercase text-armazon-400">{nota}</span>
    </div>
  );
}

function DetalleDelTitulo({
  titulo: t,
  puedeRevertir,
  alCerrar,
  alRevertido,
}: {
  titulo: Titulo;
  puedeRevertir: boolean;
  alCerrar: () => void;
  alRevertido: () => void;
}) {
  const e = t.extincion;
  const [motivo, setMotivo] = useState('');
  const [deshaciendo, setDeshaciendo] = useState(false);

  const seDeshaceAqui = e?.forma === 'PAGO_ANTERIOR';

  const revertir = useMutation({
    mutationFn: () =>
      api.post(`/titulos/pagos-anteriores/${t.id}/revertir`, { motivo: motivo.trim() }),
    onSuccess: () => {
      notificar.exito('El título vuelve a deberse', 'Marca deshecha');
      alRevertido();
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudo deshacer'),
  });

  return (
    <PanelLateral
      abierto
      alCerrar={alCerrar}
      icono={ScrollText}
      titulo={`Título ${t.codigo ?? t.id}`}
      descripcion={t.nombre ?? 'Sin nombre en el origen'}
    >
      <dl className="space-y-3 text-xs">
        <Dato rotulo="Identificación" valor={t.identificacion ?? 'Sin cédula en el origen'} />
        <Dato rotulo="Código anterior" valor={t.codigo ?? '—'} />
        <Dato rotulo="Parroquia (DPA)" valor={t.parroquia ?? '—'} />
        <Dato
          rotulo="Período"
          valor={`${enMes(t.desde)}${t.hasta ? ` – ${enMes(t.hasta)}` : ''}${
            t.meses != null ? ` · ${t.meses} meses` : ''
          }`}
        />
        <Dato rotulo="Capital" valor={enDinero(t.capital)} />
        <Dato rotulo="Saldo" valor={enDinero(t.saldo)} />
        {t.anexos && <Dato rotulo="Anexos del SIRE" valor={t.anexos} />}
      </dl>

      <div className="mt-6 rounded-xl border border-armazon-200 p-4">
        <p className="text-[10px] font-bold uppercase tracking-wide text-armazon-500">Situación</p>
        {e ? (
          <dl className="mt-2 space-y-2 text-xs">
            <Dato rotulo="Forma" valor={e.etiqueta} />
            <Dato rotulo="Fecha" valor={enFecha(e.fecha, true)} />
            <Dato rotulo="Capital" valor={enDinero(e.capital)} />
            {e.interes > 0 && (
              <Dato rotulo="Interés cobrado en su día" valor={enDinero(e.interes)} />
            )}
            {e.referencia && (
              <Dato
                rotulo={
                  e.forma === 'PAGO_ANTERIOR' ? 'Factura del sistema anterior' : 'Referencia'
                }
                valor={e.referencia}
              />
            )}
            {e.documento && <Dato rotulo="Documento" valor={e.documento} />}
            {e.observacion && <Dato rotulo="Con qué se comprobó" valor={e.observacion} />}
            {e.registradoPor && <Dato rotulo="Lo registró" valor={e.registradoPor} />}
          </dl>
        ) : (
          <p className="mt-2 text-xs font-semibold uppercase text-amber-700">
            Pendiente · se cobra el capital, sin interés
          </p>
        )}
      </div>

      {seDeshaceAqui && puedeRevertir && (
        <div className="mt-4 rounded-xl border border-armazon-200 p-4">
          <p className="text-[10px] font-bold uppercase tracking-wide text-armazon-500">
            ¿Se marcó por error?
          </p>
          {deshaciendo ? (
            <>
              <textarea
                value={motivo}
                onChange={(evento) => setMotivo(evento.target.value)}
                maxLength={300}
                rows={2}
                placeholder="Por qué se deshace…"
                className="mt-2 w-full rounded-lg border border-armazon-200 px-3 py-2 text-xs uppercase outline-none focus:border-marca-400"
              />
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  disabled={motivo.trim().length < 10 || revertir.isPending}
                  onClick={() => revertir.mutate()}
                  className="inline-flex h-9 items-center gap-2 rounded-lg bg-red-700 px-3 text-[11px] font-bold uppercase text-white hover:bg-red-800 disabled:opacity-40"
                >
                  <RotateCcw className="size-3.5" aria-hidden />
                  Deshacer: vuelve a deberse
                </button>
                <button
                  type="button"
                  onClick={() => setDeshaciendo(false)}
                  className="inline-flex h-9 items-center rounded-lg border border-armazon-200 px-3 text-[11px] font-semibold uppercase text-armazon-600 hover:bg-armazon-50"
                >
                  Dejarlo
                </button>
              </div>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setDeshaciendo(true)}
              className="mt-2 inline-flex h-9 items-center gap-2 rounded-lg border border-armazon-300 px-3 text-[11px] font-semibold uppercase text-armazon-700 hover:bg-armazon-50"
            >
              <RotateCcw className="size-3.5" aria-hidden />
              Deshacer la marca
            </button>
          )}
        </div>
      )}
    </PanelLateral>
  );
}

function Dato({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div>
      <dt className="text-[10px] font-semibold uppercase text-armazon-400">{rotulo}</dt>
      <dd className="mt-0.5 break-words uppercase text-armazon-800">{valor}</dd>
    </div>
  );
}

function Selector({
  valor,
  etiqueta,
  opciones,
  alCambiar,
}: {
  valor: string;
  etiqueta: string;
  opciones: readonly { valor: string; etiqueta: string }[];
  alCambiar: (valor: string) => void;
}) {
  return (
    <select
      value={valor}
      onChange={(evento) => alCambiar(evento.target.value)}
      aria-label={etiqueta}
      className="h-9 rounded-lg border border-armazon-200 bg-white px-2 text-xs uppercase text-armazon-600 outline-none transition-colors focus:border-marca-400"
    >
      {opciones.map((o) => (
        <option key={o.valor} value={o.valor}>
          {o.etiqueta.toLocaleUpperCase('es')}
        </option>
      ))}
    </select>
  );
}

const MESES = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];

function enMes(iso: string | undefined): string {
  if (!iso) return '—';
  const [anio, mes] = iso.split('-');
  return `${MESES[Number(mes) - 1]} ${anio}`;
}

function enFecha(iso: string, conHora = false): string {
  const [dia, hora] = iso.split('T');
  const [anio, mes, d] = (dia ?? '').split('-');
  return `${d}/${mes}/${anio}${conHora && hora ? ` ${hora.slice(0, 5)}` : ''}`;
}

function enDinero(valor: number): string {
  return `$${valor.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
